import { Validators } from '@angular/forms';

export type FieldType = 'text' | 'email' | 'url' | 'number' | 'textarea' | 'checkbox' | 'select' | 'multiselect';
export type FormTab = 'details' | 'team' | 'financials' | 'documents' | 'gallery';
export type FormGroupName = 'basicInformation' | 'address' | 'socialMediaLinks' | 'charityDetails' | 'charityLeader' | 'teamInformation' | 'executiveTeam' | 'boardMembers' | 'partnerOrganizations' | 'financialPeriod' | 'financialRevenue' | 'financialExpenses' | 'financialTotals' | 'charityDocuments' | 'charityGallery';

export interface FormFieldConfig {
  name: string;
  labelKey: string;          // Translation key for label
  type: FieldType;
  placeholderKey?: string;   // Translation key for placeholder
  defaultValue?: any;
  validators?: any[];
  maxLength?: number;
  tab: FormTab;
  group: FormGroupName;
  profileGroup?: FormGroupName; // Section this field is displayed under in the profile setup dialog, when it differs from `group` (the form-model group)
  sectionTitleKey: string;   // Translation key for section title
  key?: string; // Key for database mapping of the field
  profileFieldUuid?: string; // UUID from /api/v1/profiles/{profileUuid} innerFields — used to disambiguate fields that share the same name (e.g. fullName, email across sections)
}

/**
 * Charity Form Configuration
 * This is the single source of truth for all form fields
 * All labels and placeholders use translation keys for multi-tenant, multi-language support
 */
export const CHARITY_FORM_CONFIG: FormFieldConfig[] = [
  // ==================== DETAILS TAB ====================
  // Basic Information Section
  {
    name: 'charityProfile',
    labelKey: 'settings.charity_details_form.charity_profile_label',
    type: 'select',
    placeholderKey: 'settings.charity_details_form.select_profile',
    defaultValue: null,
    tab: 'details',
    group: 'basicInformation',
    sectionTitleKey: 'settings.charity_details_form.basic_information_title'
  },
  {
    name: 'organizationName',
    labelKey: 'settings.charity_details_form.charity_name_label',
    type: 'text',
    placeholderKey: 'settings.charity_details_form.charity_name_placeholder',
    defaultValue: '',
    validators: [Validators.required, Validators.maxLength(200)],
    maxLength: 200,
    tab: 'details',
    group: 'basicInformation',
    sectionTitleKey: 'settings.charity_details_form.basic_information_title',
    key: 'organisationName'
  },
  {
    name: 'givingLink',
    labelKey: 'settings.charity_details_form.giving_link_label',
    type: 'url',
    placeholderKey: 'settings.charity_details_form.giving_link_placeholder',
    defaultValue: '',
    tab: 'details',
    group: 'charityDetails',
    profileGroup: 'basicInformation',
    sectionTitleKey: 'settings.charity_details_form.basic_information_title',
    key: 'givingLink'
  },
  {
    name: 'private',
    labelKey: 'settings.charity_details_form.private_label',
    type: 'checkbox',
    defaultValue: false,
    tab: 'details',
    group: 'basicInformation',
    sectionTitleKey: 'settings.charity_details_form.basic_information_title',
    key: 'isPrivate',
  },

  // Address Section
  {
    name: 'streetName',
    labelKey: 'settings.charity_details_form.street_address_label',
    type: 'text',
    placeholderKey: 'settings.charity_details_form.street_address_placeholder',
    defaultValue: '',
    tab: 'details',
    group: 'address',
    sectionTitleKey: 'settings.charity_details_form.address_title',
    key: 'streetName'
  },
  {
    name: 'city',
    labelKey: 'settings.charity_details_form.city_label',
    type: 'text',
    placeholderKey: 'settings.charity_details_form.city_placeholder',
    defaultValue: '',
    tab: 'details',
    group: 'address',
    sectionTitleKey: 'settings.charity_details_form.address_title',
    key: 'city',
  },
  {
    name: 'country',
    labelKey: 'settings.charity_details_form.country_label',
    type: 'select',
    placeholderKey: 'settings.charity_details_form.country_placeholder',
    defaultValue: null,
    tab: 'details',
    group: 'address',
    sectionTitleKey: 'settings.charity_details_form.address_title',
    key: 'country'
  },
  {
    name: 'stateProvince',
    labelKey: 'settings.charity_details_form.state_label',
    type: 'text',
    placeholderKey: 'settings.charity_details_form.state_placeholder',
    defaultValue: '',
    tab: 'details',
    group: 'address',
    sectionTitleKey: 'settings.charity_details_form.address_title',
    key: 'stateProvince',
  },
  {
    name: 'landmark',
    labelKey: 'settings.charity_details_form.landmark_label',
    type: 'text',
    placeholderKey: 'settings.charity_details_form.landmark_placeholder',
    defaultValue: '',
    tab: 'details',
    group: 'address',
    sectionTitleKey: 'settings.charity_details_form.address_title',
    key: 'landmarkAddress'
  },

  // Social Media Links Section
  {
    name: 'linkedin',
    labelKey: 'settings.charity_details_form.linkedin_label',
    type: 'url',
    placeholderKey: 'settings.charity_details_form.linkedin_placeholder',
    defaultValue: '',
    tab: 'details',
    group: 'socialMediaLinks',
    sectionTitleKey: 'settings.charity_details_form.social_media_title',
    key: 'socialMediaLinkedin'
  },
  {
    name: 'twitter',
    labelKey: 'settings.charity_details_form.x_label',
    type: 'url',
    placeholderKey: 'settings.charity_details_form.x_placeholder',
    defaultValue: '',
    tab: 'details',
    group: 'socialMediaLinks',
    sectionTitleKey: 'settings.charity_details_form.social_media_title',
    key: 'socialMediaTwitter'
  },
  {
    name: 'facebook',
    labelKey: 'settings.charity_details_form.facebook_label',
    type: 'url',
    placeholderKey: 'settings.charity_details_form.facebook_placeholder',
    defaultValue: '',
    tab: 'details',
    group: 'socialMediaLinks',
    sectionTitleKey: 'settings.charity_details_form.social_media_title',
    key: 'socialMediaFacebook'
  },
  {
    name: 'instagram',
    labelKey: 'settings.charity_details_form.instagram_label',
    type: 'url',
    placeholderKey: 'settings.charity_details_form.instagram_placeholder',
    defaultValue: '',
    tab: 'details',
    group: 'socialMediaLinks',
    sectionTitleKey: 'settings.charity_details_form.social_media_title',
    key: 'socialMediaInstagram'
  },

  // Charity Details Section
  {
    name: 'website',
    labelKey: 'settings.charity_details_form.charity_website_label',
    type: 'url',
    placeholderKey: 'settings.charity_details_form.website_placeholder',
    defaultValue: '',
    tab: 'details',
    group: 'charityDetails',
    sectionTitleKey: 'settings.charity_details_form.charity_details_title',
    key: 'organisationWebsite'
  },
  {
    name: 'cause',
    labelKey: 'settings.charity_details_form.charity_cause_label',
    type: 'multiselect',
    placeholderKey: 'settings.charity_details_form.select_cause',
    defaultValue: [],
    tab: 'details',
    group: 'charityDetails',
    sectionTitleKey: 'settings.charity_details_form.charity_details_title',
    key: 'cause'
  },
  {
    name: 'category',
    labelKey: 'settings.charity_details_form.charity_category_label',
    type: 'multiselect',
    placeholderKey: 'settings.charity_details_form.select_categories_placeholder',
    defaultValue: [],
    tab: 'details',
    group: 'charityDetails',
    sectionTitleKey: 'settings.charity_details_form.charity_details_title',
    key: 'categories'
  },
  {
    name: 'influencedCountries',
    labelKey: 'settings.charity_details_form.influenced_countries_label',
    type: 'multiselect',
    placeholderKey: 'settings.charity_details_form.select_countries_placeholder',
    defaultValue: [],
    tab: 'details',
    group: 'charityDetails',
    sectionTitleKey: 'settings.charity_details_form.charity_details_title',
    key: 'organisationInfluencedCountries'
  },
  {
    name: 'targetPopulation',
    labelKey: 'settings.charity_details_form.target_population_label',
    type: 'text',
    placeholderKey: 'settings.charity_details_form.target_population_placeholder',
    defaultValue: '',
    tab: 'details',
    group: 'charityDetails',
    sectionTitleKey: 'settings.charity_details_form.charity_details_title',
    key: 'targetPopulation'
  },
  {
    name: 'yearFounded',
    labelKey: 'settings.charity_details_form.year_founded_label',
    type: 'number',
    placeholderKey: 'settings.charity_details_form.year_founded_placeholder',
    defaultValue: null,
    tab: 'details',
    group: 'charityDetails',
    sectionTitleKey: 'settings.charity_details_form.charity_details_title',
    key: 'yearFounded'
  },
  {
    name: 'executiveSummary',
    labelKey: 'settings.charity_details_form.executive_summary_label',
    type: 'textarea',
    placeholderKey: 'settings.charity_details_form.executive_summary_placeholder',
    defaultValue: '',
    validators: [Validators.maxLength(1000)],
    maxLength: 1000,
    tab: 'details',
    group: 'charityDetails',
    sectionTitleKey: 'settings.charity_details_form.charity_details_title',
    key: 'executiveSummary'
  },
  {
    name: 'visionStatement',
    labelKey: 'settings.charity_details_form.vision_statement_label',
    type: 'textarea',
    placeholderKey: 'settings.charity_details_form.vision_statement_placeholder',
    defaultValue: '',
    validators: [Validators.maxLength(1000)],
    maxLength: 1000,
    tab: 'details',
    group: 'charityDetails',
    sectionTitleKey: 'settings.charity_details_form.charity_details_title',
    key: 'visionStatement'
  },
  {
    name: 'missionStatement',
    labelKey: 'settings.charity_details_form.mission_statement_label',
    type: 'textarea',
    placeholderKey: 'settings.charity_details_form.mission_statement_placeholder',
    defaultValue: '',
    validators: [Validators.maxLength(1000)],
    maxLength: 1000,
    tab: 'details',
    group: 'charityDetails',
    sectionTitleKey: 'settings.charity_details_form.charity_details_title',
    key: 'missionStatement'
  },
  {
    name: 'history',
    labelKey: 'settings.charity_details_form.history_label',
    type: 'textarea',
    placeholderKey: 'settings.charity_details_form.history_placeholder',
    defaultValue: '',
    validators: [Validators.maxLength(1000)],
    maxLength: 1000,
    tab: 'details',
    group: 'charityDetails',
    sectionTitleKey: 'settings.charity_details_form.charity_details_title',
    key: 'history'
  },
  {
    name: 'coverPhoto',
    labelKey: 'settings.charity_details_form.cover_photo_label',
    type: 'text',
    defaultValue: '',
    tab: 'details',
    group: 'charityDetails',
    sectionTitleKey: 'settings.charity_details_form.charity_details_title',
    key: 'organisationCoverImageDto',
    profileFieldUuid: 'd8574b15-f6df-4207-b284-a8a8d4089033'
  },
  {
    name: 'logo',
    labelKey: 'settings.charity_details_form.logo_label',
    type: 'text',
    defaultValue: '',
    tab: 'details',
    group: 'charityDetails',
    sectionTitleKey: 'settings.charity_details_form.charity_details_title',
    key: 'organisationTeamMediaVideoDto',
    profileFieldUuid: 'ec41005d-cb05-42d5-8dd2-9383375166cb'
  },

  // ==================== TEAM TAB ====================
  // Charity Leader Section
  {
    name: 'leaderName',
    labelKey: 'settings.charity_details_form.full_name_label',
    type: 'text',
    placeholderKey: 'settings.charity_details_form.full_name_placeholder',
    defaultValue: '',
    tab: 'team',
    group: 'charityLeader',
    sectionTitleKey: 'settings.charity_details_form.charity_leader_title',
    key: 'fullName',
    profileFieldUuid: 'c4d14774-3d2a-4854-837f-5e67a7ee1e55'
  },
  {
    name: 'leaderEmail',
    labelKey: 'settings.charity_details_form.email_address_label',
    type: 'email',
    placeholderKey: 'settings.charity_details_form.email_placeholder',
    defaultValue: '',
    validators: [Validators.email],
    tab: 'team',
    group: 'charityLeader',
    sectionTitleKey: 'settings.charity_details_form.charity_leader_title',
    key: 'email',
    profileFieldUuid: '9d80064d-d24e-4e40-a239-ac287eaf37da'
  },
  {
    name: 'leaderLinkedin',
    labelKey: 'settings.charity_details_form.linkedin_profile_label',
    type: 'url',
    placeholderKey: 'settings.charity_details_form.linkedin_url_placeholder',
    defaultValue: '',
    tab: 'team',
    group: 'charityLeader',
    sectionTitleKey: 'settings.charity_details_form.charity_leader_title',
    key: 'linkedinProfile',
    profileFieldUuid: '33ff9128-565e-4e48-a3a3-a58691e52dd7'
  },
  {
    name: 'leaderBio',
    labelKey: 'settings.charity_details_form.bio_label',
    type: 'text',
    placeholderKey: 'settings.charity_details_form.bio_placeholder',
    defaultValue: '',
    tab: 'team',
    group: 'charityLeader',
    sectionTitleKey: 'settings.charity_details_form.charity_leader_title',
    key: 'bio',
    profileFieldUuid: '9d80064d-d24e-4e40-a239-ac287eaf37df'
  },

  // Team Information Section
  {
    name: 'numberOfTeamMembers',
    labelKey: 'settings.charity_details_form.number_of_team_members_label',
    type: 'number',
    placeholderKey: 'settings.charity_details_form.number_placeholder',
    defaultValue: '',
    tab: 'team',
    group: 'teamInformation',
    sectionTitleKey: 'settings.charity_details_form.team_information_title',
    key: 'numberOfEmployees'
  },

  // Executive Team Section
  {
    name: 'executiveName',
    labelKey: 'settings.charity_details_form.full_name_label',
    type: 'text',
    placeholderKey: 'settings.charity_details_form.full_name_placeholder',
    defaultValue: '',
    tab: 'team',
    group: 'executiveTeam',
    sectionTitleKey: 'settings.charity_details_form.executive_team_title',
    key: 'fullName',
    profileFieldUuid: '8da2bb31-e024-4654-a070-27bf7dbd78de'
  },
  {
    name: 'executiveEmail',
    labelKey: 'settings.charity_details_form.email_address_label',
    type: 'email',
    placeholderKey: 'settings.charity_details_form.email_placeholder',
    defaultValue: '',
    validators: [Validators.email],
    tab: 'team',
    group: 'executiveTeam',
    sectionTitleKey: 'settings.charity_details_form.executive_team_title',
    key: 'email',
    profileFieldUuid: '9d80064d-d24e-4e40-a239-ac287eaf37dc'
  },
  {
    name: 'executiveLinkedin',
    labelKey: 'settings.charity_details_form.linkedin_profile_label',
    type: 'url',
    placeholderKey: 'settings.charity_details_form.linkedin_url_placeholder',
    defaultValue: '',
    tab: 'team',
    group: 'executiveTeam',
    sectionTitleKey: 'settings.charity_details_form.executive_team_title',
    key: 'bio',
    profileFieldUuid: 'fd68f505-7eac-4fbe-9f2e-c8d72e29d0cb'
  },
  {
    name: 'executiveBio',
    labelKey: 'settings.charity_details_form.bio_label',
    type: 'text',
    placeholderKey: 'settings.charity_details_form.brief_description_placeholder',
    defaultValue: '',
    validators: [Validators.maxLength(500)],
    maxLength: 500,
    tab: 'team',
    group: 'executiveTeam',
    sectionTitleKey: 'settings.charity_details_form.executive_team_title',
    key: 'linkedinProfile',
    profileFieldUuid: '98cf8f64-b9a8-46dc-a240-a3fce96b01dc'
  },

  // Board Members Section
  {
    name: 'boardMemberName',
    labelKey: 'settings.charity_details_form.full_name_label',
    type: 'text',
    placeholderKey: 'settings.charity_details_form.full_name_placeholder',
    defaultValue: '',
    tab: 'team',
    group: 'boardMembers',
    sectionTitleKey: 'settings.charity_details_form.board_member_title',
    key: 'fullName',
    profileFieldUuid: '4a05e984-c830-4091-bd48-6891ff9cec10'
  },
  {
    name: 'boardMemberEmail',
    labelKey: 'settings.charity_details_form.email_address_label',
    type: 'email',
    placeholderKey: 'settings.charity_details_form.email_placeholder',
    defaultValue: '',
    validators: [Validators.email],
    tab: 'team',
    group: 'boardMembers',
    sectionTitleKey: 'settings.charity_details_form.board_member_title',
    key: 'email',
    profileFieldUuid: '9d80064d-d24e-4e40-a239-ac287eaf37db'
  },
  {
    name: 'boardMemberLinkedin',
    labelKey: 'settings.charity_details_form.linkedin_profile_label',
    type: 'url',
    placeholderKey: 'settings.charity_details_form.linkedin_url_placeholder',
    defaultValue: '',
    tab: 'team',
    group: 'boardMembers',
    sectionTitleKey: 'settings.charity_details_form.board_member_title',
    key: 'linkedinProfile',
    profileFieldUuid: 'b2353492-1d0a-4f81-a092-a8b8cc8a83cb'
  },
  {
    name: 'boardMemberBio',
    labelKey: 'settings.charity_details_form.bio_label',
    type: 'text',
    placeholderKey: 'settings.charity_details_form.brief_description_placeholder',
    defaultValue: '',
    validators: [Validators.maxLength(500)],
    maxLength: 500,
    tab: 'team',
    group: 'boardMembers',
    sectionTitleKey: 'settings.charity_details_form.board_member_title',
    key: 'bio',
    profileFieldUuid: 'd88dccda-155f-458c-9002-d03bd208a33d'
  },

  // Partner Organizations Section
  {
    name: 'partnerName',
    labelKey: 'settings.charity_details_form.charity_name_label',
    type: 'text',
    placeholderKey: 'settings.charity_details_form.charity_name_placeholder',
    defaultValue: '',
    tab: 'team',
    group: 'partnerOrganizations',
    sectionTitleKey: 'settings.charity_details_form.partner_organizations_title',
    key: 'name',
    profileFieldUuid: '2d1c0c46-99af-4577-8b33-28a4264d7e47'
  },
  {
    name: 'partnerEmail',
    labelKey: 'settings.charity_details_form.email_address_label',
    type: 'email',
    placeholderKey: 'settings.charity_details_form.email_placeholder',
    defaultValue: '',
    validators: [Validators.email],
    tab: 'team',
    group: 'partnerOrganizations',
    sectionTitleKey: 'settings.charity_details_form.partner_organizations_title',
    key: 'email',
    profileFieldUuid: '9d80064d-d24e-4e40-a239-ac287eaf37dd'
  },
  {
    name: 'partnerDescription',
    labelKey: 'settings.charity_details_form.relationship_description_label',
    type: 'text',
    placeholderKey: 'settings.charity_details_form.relationship_description_placeholder',
    defaultValue: '',
    validators: [Validators.maxLength(500)],
    maxLength: 500,
    tab: 'team',
    group: 'partnerOrganizations',
    sectionTitleKey: 'settings.charity_details_form.partner_organizations_title',
    key: 'relationshipDescription'
  },

  // ==================== FINANCIALS TAB ====================

  // Financial Period Section (Budget Year / Currency / Fiscal Year End)
  {
    name: 'budgetYear',
    labelKey: 'settings.charity_profile_setup.financials.budget_year_label',
    type: 'text',
    defaultValue: '',
    tab: 'financials',
    group: 'financialPeriod',
    sectionTitleKey: 'settings.charity_profile_setup.financials.section_period',
    key: 'budgetYear',
    profileFieldUuid: '43f3d7dc-d52d-4f45-b0ef-c4529afffc74'
  },
  {
    name: 'fiscalYearEnd',
    labelKey: 'settings.charity_profile_setup.financials.fiscal_year_end_label',
    type: 'text',
    defaultValue: '',
    tab: 'financials',
    group: 'financialPeriod',
    sectionTitleKey: 'settings.charity_profile_setup.financials.section_period',
    key: 'fiscalYearEnd',
    profileFieldUuid: '8ce7ccfa-8acd-4f2f-8a95-3af5f35c59f7'
  },
  {
    name: 'budgetCurrency',
    labelKey: 'settings.charity_profile_setup.financials.currency_label',
    type: 'text',
    defaultValue: '',
    tab: 'financials',
    group: 'financialPeriod',
    sectionTitleKey: 'settings.charity_profile_setup.financials.section_period',
    key: 'budgetCurrency',
    profileFieldUuid: '0a772154-67b9-44fc-9396-ab8da24877e6'
  },

  // Revenue Section
  {
    name: 'revenueItemName',
    labelKey: 'settings.charity_profile_setup.financials.revenue_item_name_label',
    type: 'text',
    defaultValue: '',
    tab: 'financials',
    group: 'financialRevenue',
    sectionTitleKey: 'settings.charity_profile_setup.financials.section_revenue',
    key: 'itemName',
    profileFieldUuid: '233d7f2a-e18b-447e-9cce-cf024eec3b11'
  },
  {
    name: 'revenueBudget',
    labelKey: 'settings.charity_profile_setup.financials.revenue_budget_label',
    type: 'number',
    defaultValue: '',
    tab: 'financials',
    group: 'financialRevenue',
    sectionTitleKey: 'settings.charity_profile_setup.financials.section_revenue',
    key: 'budget',
    profileFieldUuid: '175ce554-d1d7-4ce1-9988-1a076770ee6f'
  },
  {
    name: 'revenueActuals',
    labelKey: 'settings.charity_profile_setup.financials.revenue_actuals_label',
    type: 'number',
    defaultValue: '',
    tab: 'financials',
    group: 'financialRevenue',
    sectionTitleKey: 'settings.charity_profile_setup.financials.section_revenue',
    key: 'actuals',
    profileFieldUuid: '155781c9-3bfc-4d61-95df-2a15aae13a86'
  },

  // Expenses Section
  {
    name: 'expenseItemName',
    labelKey: 'settings.charity_profile_setup.financials.expense_item_name_label',
    type: 'text',
    defaultValue: '',
    tab: 'financials',
    group: 'financialExpenses',
    sectionTitleKey: 'settings.charity_profile_setup.financials.section_expenses',
    key: 'itemName',
    profileFieldUuid: '7269eb3c-73d2-4d1f-a3ca-85865e5760e1'
  },
  {
    name: 'expenseBudget',
    labelKey: 'settings.charity_profile_setup.financials.expense_budget_label',
    type: 'number',
    defaultValue: '',
    tab: 'financials',
    group: 'financialExpenses',
    sectionTitleKey: 'settings.charity_profile_setup.financials.section_expenses',
    key: 'budget',
    profileFieldUuid: 'a897ebab-1b54-4793-bc57-944481c6c6f8'
  },
  {
    name: 'expenseActuals',
    labelKey: 'settings.charity_profile_setup.financials.expense_actuals_label',
    type: 'number',
    defaultValue: '',
    tab: 'financials',
    group: 'financialExpenses',
    sectionTitleKey: 'settings.charity_profile_setup.financials.section_expenses',
    key: 'actuals',
    profileFieldUuid: '87179154-27b1-415e-8e0f-4105f5af6d41'
  },

  // Financial Totals Section
  {
    name: 'totalBudgetRevenues',
    labelKey: 'settings.charity_profile_setup.financials.total_budget_revenues_label',
    type: 'number',
    defaultValue: '',
    tab: 'financials',
    group: 'financialTotals',
    sectionTitleKey: 'settings.charity_profile_setup.financials.section_totals',
    key: 'totalBudgetRevenues',
    profileFieldUuid: '27813a04-5094-47a5-aaab-bd2a5047711f'
  },
  {
    name: 'totalActualsRevenues',
    labelKey: 'settings.charity_profile_setup.financials.total_actuals_revenues_label',
    type: 'number',
    defaultValue: '',
    tab: 'financials',
    group: 'financialTotals',
    sectionTitleKey: 'settings.charity_profile_setup.financials.section_totals',
    key: 'totalActualsRevenues',
    profileFieldUuid: '837eef14-4ba0-4a24-8015-9d1268cbe652'
  },
  {
    name: 'totalBudgetExpenses',
    labelKey: 'settings.charity_profile_setup.financials.total_budget_expenses_label',
    type: 'number',
    defaultValue: '',
    tab: 'financials',
    group: 'financialTotals',
    sectionTitleKey: 'settings.charity_profile_setup.financials.section_totals',
    key: 'totalBudgetExpenses',
    profileFieldUuid: 'bce147fd-04aa-478a-a858-e1241ca40739'
  },
  {
    name: 'totalActualsExpenses',
    labelKey: 'settings.charity_profile_setup.financials.total_actuals_expenses_label',
    type: 'number',
    defaultValue: '',
    tab: 'financials',
    group: 'financialTotals',
    sectionTitleKey: 'settings.charity_profile_setup.financials.section_totals',
    key: 'totalActualsExpenses',
    profileFieldUuid: '0bbfe323-806d-40c3-ac1e-3205128ef59c'
  },
  {
    name: 'netGainLossBudget',
    labelKey: 'settings.charity_profile_setup.financials.net_gain_loss_budget_label',
    type: 'number',
    defaultValue: '',
    tab: 'financials',
    group: 'financialTotals',
    sectionTitleKey: 'settings.charity_profile_setup.financials.section_totals',
    key: 'netGainLossBudget',
    profileFieldUuid: '789ed84c-6dec-4dcb-8e3b-ecd5032f3536'
  },
  {
    name: 'netGainLossActuals',
    labelKey: 'settings.charity_profile_setup.financials.net_gain_loss_actuals_label',
    type: 'number',
    defaultValue: '',
    tab: 'financials',
    group: 'financialTotals',
    sectionTitleKey: 'settings.charity_profile_setup.financials.section_totals',
    key: 'netGainLossActuals',
    profileFieldUuid: '60a586bc-a160-4dc5-8084-12b3c9fdc209'
  },

  // ==================== DOCUMENTS TAB ====================

  // Documents Section
  {
    name: 'organisationDocuments',
    labelKey: 'settings.charity_details_form.documents.description',
    type: 'text',
    defaultValue: '',
    tab: 'documents',
    group: 'charityDocuments',
    sectionTitleKey: 'settings.charity_profile_setup.tab_documents',
    key: 'organisationDocuments'
  },

  // ==================== GALLERY TAB ====================

  // Gallery Section
  {
    name: 'organisationImages',
    labelKey: 'settings.charity_details_form.gallery.description',
    type: 'text',
    defaultValue: '',
    tab: 'gallery',
    group: 'charityGallery',
    sectionTitleKey: 'settings.charity_profile_setup.tab_gallery',
    key: 'organisationImages'
  }
];

/**
 * Maps config group names to their actual schema paths in the API.
 * Needed because the schema uses different names (e.g. 'organisationLeader')
 * than the form config (e.g. 'charityLeader'), causing group-path UUID lookups
 * to silently fail and fall back to ambiguous plain-key matches.
 */
export const SCHEMA_GROUP_PATH: Partial<Record<FormGroupName, string>> = {
  charityLeader: 'organisationLeader',
  executiveTeam: 'executiveTeams',
  partnerOrganizations: 'partnerOrganisations',
  financialPeriod: 'budgetOrganisations',
  financialRevenue: 'budgetOrganisations.budgetRevenues',
  financialExpenses: 'budgetOrganisations.budgetExpenses',
  financialTotals: 'budgetOrganisations',
};

/**
 * Helper function to get fields by tab
 */
export function getFieldsByTab(tab: FormTab): FormFieldConfig[] {
  return CHARITY_FORM_CONFIG.filter(field => field.tab === tab);
}

/**
 * Helper function to get fields by group
 */
export function getFieldsByGroup(group: FormGroupName): FormFieldConfig[] {
  return CHARITY_FORM_CONFIG.filter(field => field.group === group);
}

/**
 * Section a field is displayed under in the profile setup dialog.
 * Falls back to the form-model group when no display override is set.
 */
export function getProfileGroup(field: FormFieldConfig): FormGroupName {
  return field.profileGroup ?? field.group;
}

/**
 * Helper function to get fields for a profile setup dialog section
 */
export function getProfileFieldsByGroup(group: FormGroupName): FormFieldConfig[] {
  return CHARITY_FORM_CONFIG.filter(field => getProfileGroup(field) === group);
}

/**
 * Helper function to get unique sections for a tab
 */
export function getSectionsForTab(tab: FormTab): { id: string; title: string; group: FormGroupName }[] {
  const fields = getFieldsByTab(tab);
  const uniqueSections = new Map<string, { id: string; title: string; group: FormGroupName }>();

  fields.forEach(field => {
    const group = getProfileGroup(field);
    const key = `${group}`;
    if (!uniqueSections.has(key)) {
      uniqueSections.set(key, {
        id: group,
        title: field.sectionTitleKey,
        group: group
      });
    }
  });

  return Array.from(uniqueSections.values());
}

/**
 * Helper function to build FormGroup structure from config
 */
export function buildFormGroupStructure(): any {
  const structure: any = {};

  // Get unique groups
  const groups = [...new Set(CHARITY_FORM_CONFIG.map(f => f.group))];

  groups.forEach(group => {
    const groupFields = getFieldsByGroup(group);
    const groupControls: any = {};

    groupFields.forEach(field => {
      groupControls[field.name] = [field.defaultValue, field.validators || []];
    });

    structure[group] = groupControls;
  });

  return structure;
}
